import * as React from "react";
import { useState, useEffect } from "react";
import { useFeesState } from "../../../store/useFees";
import { useFeeId, usePatchFees } from "../../../sevices/hooks/admin";
import DeleteFessPopup from "./DeleteFeesPopup";

export interface FeeValue {
  amount: string;
  baseValue?: string;
  currency?: string;
}

export interface IPatch {
  type: string;
  description: string;
  value: FeeValue;
}

export interface FeeAccordionProps {
  title: string;
  value: string;
  type: string;
  description?: string;
  setActive: (arg: string) => void;
  active: string;
  id: string;
  isLoading?: boolean;
}

const FeesAccordion: React.FC<FeeAccordionProps> = ({
  title,
  value,
  type,
  description = "",
  setActive,
  active,
  id,
  isLoading = false
}) => {
  const setFeesId = useFeesState((state) => state.setFeeId);
  const feeId = useFeesState((state) => state.feeId);

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [feeType, setFeeType] = useState("");
  const [feeDescription, setFeeDescription] = useState("");
  const [feeAmount, setFeeAmount] = useState<FeeValue>({
    amount: "",
    baseValue: "",
    currency: "",
  });

  const { data: feeData, isLoading: isFeeLoading } = useFeeId(id);
  const patchFees = usePatchFees(feeId);

  const isExpanded = active === title;

  // Initialize form with existing data when accordion expands
  useEffect(() => {
    if (isExpanded && feeData?.data) {
      const fee = feeData.data;
      setFeeType(fee.type || type);
      setFeeDescription(fee.description || description);
      setFeeAmount({
        amount: fee.value?.amount || value,
        baseValue: fee.value?.baseValue || "",
        currency: fee.value?.currency || ""
      });
    }
  }, [isExpanded, feeData, title, value, type, description]);

  const handleFeeTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setFeeType(e.target.value);
  };

  const handleFeeDescriptionChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFeeDescription(e.target.value);
  };

  const handleFeeAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFeeAmount({ ...feeAmount, amount: e.target.value });
  };

  const handleToggleAccordion = () => {
    if (!isExpanded) {
      setFeesId(id);
      setActive(title);
    } else {
      setActive("");
    }
  };

  const handleSubmit = async () => {
    const updateData: IPatch = {
      type: feeType,
      description: feeDescription,
      value: feeAmount
    };

    try {
      await patchFees.mutateAsync(updateData);
      setActive("");
    } catch (error) {
      console.error("Failed to update fee:", error);
    }
  };

  if (isLoading) {
    return <SkeletonLoader />;
  }

  return (
    <div className="bg-[#F7FBFB] rounded-md w-full md:max-w-2xl h-fit px-5 py-3 flex flex-col mb-4">
      <div className="flex items-center justify-between mb-2">
        <h1 className="text-[#2B2B2B] font-medium text-xl">{title}</h1>
        <button
          onClick={handleToggleAccordion}
          className="text-[#2B2B2B] text-xs border border-[#0eb683] bg-[#fff] font-medium px-3 py-0.5 rounded-sm transition duration-500 active:scale-90"
        >
          {isExpanded ? "X" : "Edit"}
        </button>
      </div>

      {!isExpanded && (
        <div className="flex flex-col gap-1">
          <h3 className="text-[#676767] text-xs font-normal capitalize">
            {type}
          </h3>
          <p className="text-[#071827] text-sm font-medium">{value}</p>
        </div>
      )}

      {isExpanded && (
        <div className="transition-all duration-300 ease-in-out">
          <div className="my-4">
            <label className="block text-sm font-medium mb-1">Fee Type</label>
            <select
              className="w-full p-2 border rounded bg-white focus:outline-none focus:ring-2 focus:ring-[#0eb683]"
              value={feeType}
              onChange={handleFeeTypeChange}
            >
              <option value="">Select Type</option>
              <option value="percentage">Percentage</option>
              <option value="fixed">Fixed</option>
              <option value="proportional">Proportional</option>
            </select>
          </div>

          <div className="my-4">
            <label className="block text-sm font-medium mb-1">Description</label>
            <input
              type="text"
              className="w-full p-2 border rounded bg-white focus:outline-none focus:ring-2 focus:ring-[#0eb683]"
              value={feeDescription}
              onChange={handleFeeDescriptionChange}
              placeholder="Enter Fee Description"
            />
          </div>

          <div className="my-4">
            <label className="block text-sm font-medium mb-1">Fee Amount</label>
            <input
              type="text"
              className="w-full p-2 border rounded bg-white focus:outline-none focus:ring-2 focus:ring-[#0eb683]"
              value={feeAmount.amount}
              onChange={handleFeeAmountChange}
              placeholder="Enter the amount you are charging"
            />
          </div>

          <div className="flex space-x-4 mt-6">
            <button
              onClick={() => setDeleteModalOpen(true)}
              className="border border-[#fb2626] bg-[#fb2626] px-5 py-2 rounded-md transition duration-300 active:scale-95 text-white font-medium"
            >
              Delete
            </button>
            <button
              onClick={handleSubmit}
              disabled={patchFees.isLoading}
              className="text-white border border-[#0eb683] bg-[#0eb683] px-7 py-2 rounded-md transition duration-300 active:scale-95 font-medium flex items-center justify-center"
            >
              {patchFees.isLoading ? (
                <span className="inline-block h-4 w-4 rounded-full border-2 border-white border-t-transparent animate-spin mr-2"></span>
              ) : null}
              Update
            </button>
          </div>
        </div>
      )}

      {deleteModalOpen && (
        <DeleteFessPopup
          id={feeId}
          isOpen={deleteModalOpen}
          onClose={() => setDeleteModalOpen(false)}
        />
      )}
    </div>
  );
};

// Skeleton loader component
const SkeletonLoader = () => {
  return (
    <div className="bg-[#F7FBFB] rounded-md w-full md:max-w-2xl h-fit px-5 py-3 flex flex-col mb-4 animate-pulse">
      <div className="flex items-center justify-between mb-2">
        <div className="h-6 bg-gray-200 rounded w-1/3"></div>
        <div className="h-6 bg-gray-200 rounded w-16"></div>
      </div>
      <div className="flex flex-col gap-1">
        <div className="h-4 bg-gray-200 rounded w-1/4 my-1"></div>
        <div className="h-5 bg-gray-200 rounded w-1/2 my-1"></div>
      </div>
    </div>
  );
};

export default FeesAccordion;